import { useEffect, useState } from 'react';
import { getQueryDefinition, fetchRegionCountryData  } from '@intelligence/services/common';

import type { IRegion } from '@intelligence/types/common';

const MOBILE_STORE_RANK_COUNTRIES_KEY = 'mobile_store_rank_countries';

const normalizeCountryCode = (code?: string): string => {
  return `${code || ''}`.trim().toLowerCase();
};

const formatMobileStoreRankCountries = (payload?: string): string[] => {
  const countries = payload?.split(',') || [];
  return countries.map(normalizeCountryCode).filter(Boolean);
};

const fetchMobileStoreRankCountries = async (): Promise<string[]> => {
  const response = await getQueryDefinition(
    {
      mobileStoreRankCountries: {
        target: 'game_config',
        params: {
          key: MOBILE_STORE_RANK_COUNTRIES_KEY,
        },
      },
    },
    false,
  );

  return formatMobileStoreRankCountries(response?.data?.mobileStoreRankCountries?.[MOBILE_STORE_RANK_COUNTRIES_KEY]);
};

const filterRegionOptionsByCountries = (regions: IRegion[], countries: string[]): IRegion[] => {
  if (!Array.isArray(regions) || regions.length === 0) {
    return [];
  }

  if (!Array.isArray(countries) || countries.length === 0) {
    return [];
  }

  const countrySet = new Set(countries);
  const result: IRegion[] = [];

  regions.forEach((region) => {
    const nextCountrySet = region?.country_set?.filter((country) => {
      return countrySet.has(normalizeCountryCode(country?.country_code));
    });

    if (!nextCountrySet?.length) {
      return;
    }

    result.push({
      ...region,
      country_set: nextCountrySet,
    });
  });

  return result;
};

export default function useMobileStoreRankRegionOptions() {
  const [isLoading, setIsLoading] = useState(true);
  const [regionOptions, setRegionOptions] = useState<IRegion[]>([]);

  useEffect(() => {
    let isMounted = true;

    const updateRegionOptions = async () => {
      try {
        setIsLoading(true);
        const [allRegionOptions, mobileStoreRankCountries] = await Promise.all([
          fetchRegionCountryData(),
          fetchMobileStoreRankCountries(),
        ]);

        if (!isMounted) {
          return;
        }

        const nextRegionOptions = filterRegionOptionsByCountries(
          allRegionOptions as IRegion[],
          mobileStoreRankCountries,
        );

        setRegionOptions(nextRegionOptions);
      } catch {
        if (!isMounted) {
          return;
        }

        setRegionOptions([]);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    updateRegionOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    isLoading,
    regionOptions,
  };
}
